/**
 * 临时 ID + 昵称持久化 (SDD-14 §1)
 * - 优先 localStorage
 * - 异常降级 sessionStorage
 * - 再异常降级内存
 */

const TOKEN_KEY = 'pigwords.playerToken';
const NICK_KEY = 'pigwords.nickname';
const LAST_EVENT_ID_KEY = 'pigwords.lastEventId';

export type StorageMode = 'local' | 'session' | 'memory';

const memoryStore = new Map<string, string>();

let detectedMode: StorageMode | null = null;

function tryWrite(storage: Storage | null, key: string, value: string): boolean {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return storage.getItem(key) === value;
  } catch {
    return false;
  }
}

function probe(storage: Storage | null): boolean {
  const probeKey = '__pigwords_probe__';
  if (!tryWrite(storage, probeKey, '1')) return false;
  try {
    storage?.removeItem(probeKey);
  } catch {
    // ignore
  }
  return true;
}

export function detectStorageMode(): StorageMode {
  if (detectedMode) return detectedMode;
  if (typeof window === 'undefined') {
    detectedMode = 'memory';
    return detectedMode;
  }
  if (probe(window.localStorage)) {
    detectedMode = 'local';
  } else if (probe(window.sessionStorage)) {
    detectedMode = 'session';
  } else {
    detectedMode = 'memory';
  }
  return detectedMode;
}

/** Used in tests */
export function resetStorageMode(): void {
  detectedMode = null;
  memoryStore.clear();
}

function getStore(): Storage | Map<string, string> {
  const mode = detectStorageMode();
  if (mode === 'local') return window.localStorage;
  if (mode === 'session') return window.sessionStorage;
  return memoryStore;
}

function read(key: string): string | null {
  const store = getStore();
  if (store instanceof Map) return store.get(key) ?? null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  const store = getStore();
  if (store instanceof Map) {
    store.set(key, value);
    return;
  }
  try {
    store.setItem(key, value);
  } catch {
    // 落到内存兜底
    memoryStore.set(key, value);
  }
}

// ───────── UUIDv4 ─────────

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidV4(s: string): boolean {
  return UUID_V4.test(s);
}

export function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // 兜底：手写 v4（仅在没有 crypto.randomUUID 的极旧环境使用）
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function defaultNicknameFor(token: string): string {
  return `猪友#${token.slice(-4).toUpperCase()}`;
}

// ───────── public API ─────────

export interface PlayerIdentity {
  token: string;
  nickname: string;
  isFreshIdentity: boolean;
}

export function getOrCreateIdentity(): PlayerIdentity {
  let token = read(TOKEN_KEY);
  let isFresh = false;
  if (!token || !isUuidV4(token)) {
    token = generateUuidV4();
    write(TOKEN_KEY, token);
    isFresh = true;
  }
  let nickname = read(NICK_KEY);
  if (!nickname) {
    nickname = defaultNicknameFor(token);
    write(NICK_KEY, nickname);
  }
  return { token, nickname, isFreshIdentity: isFresh };
}

export function saveNickname(nickname: string): void {
  write(NICK_KEY, nickname);
}

export function getLastEventId(): number {
  const raw = read(LAST_EVENT_ID_KEY);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function saveLastEventId(eventId: number): void {
  if (!Number.isFinite(eventId) || eventId < 0) return;
  write(LAST_EVENT_ID_KEY, String(eventId));
}
